<script lang="ts" setup>
import type { ISonuc } from '@/model/sonuc'
import { SonucService } from '@/services/sonuc'
import { ErrorPopup } from '@/utils/popup'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { saatFormat, saniyeFormat } from '@/utils/ExDate.ts'
import formatNumber from '@/utils/ExFormat.ts'
import CevapAnahtari from './CevapAnahtari.vue'
const router = useRouter()
const sonuc = ref<ISonuc | null>(null)

interface Props {
  siralamaAciklandiMi: boolean
}
defineProps<Props>()
const emit = defineEmits(['update:loading'])

onMounted(async () => {
  emit('update:loading', true)
  const [sonucErr, sonucData] = await SonucService.getSonuc()
  emit('update:loading', false)
  if (sonucErr) {
    ErrorPopup(sonucErr as string)
  }
  sonuc.value = sonucData as ISonuc | null
})
</script>

<template>
  <!-- Diskalifiye/uyarı: en yüksek öncelik. Sıralama ve puan gösterilmez, sadece uyarı metni. -->
  <div v-if="sonuc?.diskalifiye && sonuc?.uyari_metni"
    class="w-full p-4 md:p-6 rounded-xl border border-red-200 bg-red-50">
    <div class="flex flex-row items-center gap-3 w-full">
      <div class="rounded-full p-3 bg-red-100 h-12 min-w-12 flex items-center justify-center shrink-0">
        <MIcon name="error" size="22px" class="text-red-600" />
      </div>
      <p class="text-body-md text-red-700 text-left whitespace-pre-line">{{ sonuc.uyari_metni }}</p>
    </div>
    <button class="btn btn-status-default w-full mt-6" @click="router.push('/sonuc')">
      {{ $t('result.review_questions') }}
    </button>
  </div>

  <div v-else-if="sonuc?.katildi_mi && sonuc?.puan == 0 && sonuc?.yanlis_sayisi == 0 || !sonuc?.katildi_mi || siralamaAciklandiMi"
    class="card w-full p-3 md:p-6 space-y-2 flex flex-col items-center justify-center px-4 md:px-6">

    <div v-if="sonuc?.katildi_mi && sonuc?.puan == 0 && sonuc?.yanlis_sayisi == 0" class="flex flex-col items-center justify-center w-full">
      <div class="gap-2 rounded-full p-3 bg-gray-50 h-14 min-w-14 flex items-center justify-center">
        <MIcon name="sentiment_dissatisfied" size="20px" class="text-gray-800" />
      </div>
      <div class="flex flex-col items-center justify-center mt-4">
        <p class="text-title-lg text-gray-700">{{ $t('result.competition_ended') }}</p>
        <p class="text-body-md text-gray-700">{{ $t('result.no_marking_score') }}</p>
      </div>
       <button class="btn btn-status-default w-full mt-10" @click="router.push('/sonuc')">
        {{ $t('result.review_questions') }}
      </button>
    </div>

    <div v-else-if="!sonuc?.katildi_mi" class="flex flex-col items-center justify-center w-full">
      <div class="gap-2 rounded-full p-3 bg-gray-50 h-14 min-w-14 flex items-center justify-center">
        <MIcon name="sentiment_dissatisfied" size="20px" class="text-gray-800" />
      </div>
      <div class="flex flex-col items-center justify-center mt-4">
        <p class="text-title-lg text-gray-700">{{ $t('result.competition_ended') }}</p>
        <p class="text-body-md text-gray-700">{{ $t('result.did_not_participate') }}</p>
      </div>
       <button class="btn btn-status-default w-full mt-10" @click="router.push('/sonuc')">
        {{ $t('result.review_questions') }}
      </button>
    </div>

    <div v-else-if="siralamaAciklandiMi" class="w-full">
      <div class="flex flex-col items-center justify-center w-full">
        <div
          class="gap-2 rounded-full p-3 bg-gray-50 h-14 min-w-14 flex items-center justify-center"
        >
          <p class="text-display-sm text-gray-700">
            {{ formatNumber(sonuc?.siralama) || '-' }}
          </p>
        </div>

        <div class="flex flex-col items-center justify-center mt-4">
          <p class="text-title-lg text-gray-700">{{ $t('result.congratulations') }}</p>
          <p class="text-body-md text-gray-700">
            {{ $t('result.rank_description', { rank: sonuc?.siralama || '-' }) }}
          </p>
        </div>
      </div>

      <div
        v-if="sonuc?.siralama != null && sonuc.siralama > 0"
        class="flex flex-col items-center justify-center mt-10 w-full gap-2"
      >
        <h3 class="text-label-md text-gray-700 text-left pb-2 border-b border-gray-200 w-full">
          {{ $t('result.ranking') }}
        </h3>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.general_ranking') }}</p>
          <p class="text-body-md text-blue-600">{{ sonuc.siralama || '-' }}</p>
        </div>
        <div
          v-if="sonuc?.ulke_siralama != null && sonuc.ulke_siralama > 0"
          class="flex flex-row items-center justify-between mt-2 w-full"
        >
          <p class="text-body-md text-gray-700">{{ $t('result.country_ranking') }}</p>
          <p class="text-body-md text-blue-600">{{ sonuc.ulke_siralama || '-' }}</p>
        </div>
        <div
          v-if="sonuc?.il_siralama != null && sonuc.il_siralama > 0"
          class="flex flex-row items-center justify-between mt-2 w-full"
        >
          <p class="text-body-md text-gray-700">{{ $t('result.province_ranking') }}</p>
          <p class="text-body-md text-blue-600">{{ sonuc.il_siralama || '-' }}</p>
        </div>
        <div
          v-if="sonuc?.ilce_siralama != null && sonuc.ilce_siralama > 0"
          class="flex flex-row items-center justify-between mt-2 w-full"
        >
          <p class="text-body-md text-gray-700">{{ $t('result.district_ranking') }}</p>
          <p class="text-body-md text-blue-600">{{ sonuc.ilce_siralama || '-' }}</p>
        </div>
        <div
          v-if="sonuc?.okul_siralama != null && sonuc.okul_siralama > 0"
          class="flex flex-row items-center justify-between mt-2 w-full"
        >
          <p class="text-body-md text-gray-700">{{ $t('result.school_ranking') }}</p>
          <p class="text-body-md text-blue-600">{{ sonuc.okul_siralama || '-' }}</p>
        </div>
      </div>

      <div class="flex flex-col items-center justify-center mt-10 w-full gap-2">
        <h3 class="text-label-md text-gray-700 text-left pb-2 border-b border-gray-200 w-full">
          {{ $t('result.results') }}
        </h3>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.score') }}</p>
          <p class="text-body-md text-gray-400">{{ sonuc?.puan}}</p>
        </div>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.correct') }}</p>
          <p class="text-body-md text-green-600">{{ sonuc?.dogru_sayisi  }}</p>
        </div>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.incorrect') }}</p>
          <p class="text-body-md text-red-600">{{ sonuc?.yanlis_sayisi }}</p>
        </div>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.empty') }}</p>
          <p class="text-body-md text-gray-400">{{ sonuc?.bos_sayisi }}</p>
        </div>
      </div>

      <div class="flex flex-col items-center justify-center mt-10 w-full gap-2">
        <h3 class="text-label-md text-gray-700 text-left pb-2 border-b border-gray-200 w-full">
          {{ $t('result.timing') }}
        </h3>

        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.exam_duration') }}</p>
          <p class="text-body-md text-gray-400">{{ saniyeFormat(sonuc?.toplam_sure) || '-' }}</p>
        </div>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.exam_start') }}</p>
          <p class="text-body-md text-gray-400">{{ saatFormat(sonuc?.baslama_zamani) || '-' }}</p>
        </div>
        <div class="flex flex-row items-center justify-between mt-2 w-full">
          <p class="text-body-md text-gray-700">{{ $t('result.exam_end') }}</p>
          <p class="text-body-md text-gray-400">{{ saatFormat(sonuc?.bitirme_zamani) || '-' }}</p>
        </div>
      </div>

      <button class="btn btn-status-default w-full mt-10" @click="router.push('/sonuc')">
        {{ $t('result.review_questions') }}
      </button>
    </div>
  </div>

  <div v-else class="w-full">
    <CevapAnahtari  :sonuc="sonuc" />
  </div>
</template>
